import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

const CART_COUNT_STORAGE_KEY = 'cart-count';

/**
 * A custom element that displays a cart icon.
 *
 * VELUNE CART COUNT SAFETY V2
 * - Ignores failed add-to-cart events.
 * - Keeps the exact numeric cart count even when the visual bubble becomes a dot at 100+.
 * - Uses the resulting cart total, not the incoming product-form delta, for visibility/state.
 * - Falls back to /cart.js only in the rare case where a 100+ cart is restored without cached count data.
 * - Treats sessionStorage as optional so privacy/storage restrictions never break the cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /** @type {number | null} */
  #cartCount = null;

  /** @type {Promise<number> | null} */
  #cartCountRequest = null;

  /** @type {number} */
  get currentCartCount() {
    if (Number.isFinite(this.#cartCount)) {
      return /** @type {number} */ (this.#cartCount);
    }

    const dataCount = Number.parseInt(this.dataset.cartCount || '', 10);
    if (Number.isFinite(dataCount) && dataCount >= 0) {
      this.#cartCount = dataCount;
      return dataCount;
    }

    const visibleText = this.refs.cartBubbleCount?.textContent?.trim() || '';
    const visibleCount = Number.parseInt(visibleText, 10);

    if (Number.isFinite(visibleCount) && visibleCount >= 0) {
      this.#cartCount = visibleCount;
      this.dataset.cartCount = String(visibleCount);
      return visibleCount;
    }

    const storedCount = this.#readStoredCount();
    if (storedCount !== null) {
      this.#cartCount = storedCount;
      this.dataset.cartCount = String(storedCount);
      return storedCount;
    }

    return 0;
  }

  set currentCartCount(value) {
    const parsed = Number(value);
    const normalized = Number.isFinite(parsed) ? Math.max(0, Math.trunc(parsed)) : 0;

    this.#cartCount = normalized;
    this.dataset.cartCount = String(normalized);

    // Preserve the theme's compact dot treatment for carts containing 100+ items.
    this.refs.cartBubbleCount.textContent = normalized < 100 ? String(normalized) : '';
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.addEventListener('pageshow', this.onPageShow);

    this.#seedCartCount();
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.removeEventListener('pageshow', this.onPageShow);
  }

  #seedCartCount() {
    const visibleText = this.refs.cartBubbleCount?.textContent?.trim() || '';
    const visibleCount = Number.parseInt(visibleText, 10);

    if (Number.isFinite(visibleCount) && visibleCount >= 0) {
      this.currentCartCount = visibleCount;
      return;
    }

    const storedCount = this.#readStoredCount();
    if (storedCount !== null) {
      this.currentCartCount = storedCount;
      return;
    }

    if (!this.classList.contains('header-actions__cart-icon--has-cart')) {
      this.currentCartCount = 0;
    }
  }

  #readStoredCount() {
    try {
      const raw = sessionStorage.getItem(CART_COUNT_STORAGE_KEY);
      if (!raw) return null;

      const { value, timestamp } = JSON.parse(raw);
      const count = Number.parseInt(value, 10);
      const storedAt = Number(timestamp);

      if (!Number.isFinite(count) || count < 0) return null;
      if (!Number.isFinite(storedAt) || Date.now() - storedAt >= 10000) return null;

      return count;
    } catch (_) {
      return null;
    }
  }

  async #resolveCurrentCartCount() {
    if (Number.isFinite(this.#cartCount)) {
      return /** @type {number} */ (this.#cartCount);
    }

    const storedCount = this.#readStoredCount();
    if (storedCount !== null) {
      this.currentCartCount = storedCount;
      return storedCount;
    }

    // A visually empty count while the icon has-cart class is the theme's 100+ state.
    // Fetch only in this rare recovery case so normal cart interactions stay request-free.
    if (!this.classList.contains('header-actions__cart-icon--has-cart')) {
      this.currentCartCount = 0;
      return 0;
    }

    if (this.#cartCountRequest) return this.#cartCountRequest;

    this.#cartCountRequest = (async () => {
      try {
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${root}cart.js`, {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
        });

        if (!response.ok) throw new Error(`Cart count sync failed (${response.status})`);

        const cart = await response.json();
        const count = Number(cart?.item_count);
        const normalized = Number.isFinite(count) ? Math.max(0, Math.trunc(count)) : 0;

        this.currentCartCount = normalized;
        return normalized;
      } catch (_) {
        // Keep the icon usable even if the recovery request fails.
        return 0;
      } finally {
        this.#cartCountRequest = null;
      }
    })();

    return this.#cartCountRequest;
  }

  /**
   * Handles the page show event when the page is restored from cache.
   * @param {PageTransitionEvent} event - The page show event.
   */
  onPageShow = (event) => {
    if (event.persisted) {
      this.ensureCartBubbleIsCorrect();
    }
  };

  /**
   * Handles the cart update event.
   * @param {CartUpdateEvent} event - The cart update event.
   */
  onCartUpdate = (event) => {
    if (event.detail?.data?.didError) return;

    const comingFromProductForm = event.detail?.data?.source === 'product-form-component';
    const rawItemCount = comingFromProductForm
      ? event.detail?.data?.itemCount
      : event.detail?.data?.itemCount ?? event.detail?.resource?.item_count;

    const itemCount = Number(rawItemCount);
    if (!Number.isFinite(itemCount) || itemCount < 0) return;

    void this.renderCartBubble(Math.trunc(itemCount), comingFromProductForm);
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - Delta for product-form events, absolute count otherwise.
   * @param {boolean} comingFromProductForm - Whether the cart update is coming from the product form.
   * @param {boolean} animate - Whether to animate the bubble.
   */
  renderCartBubble = async (itemCount, comingFromProductForm, animate = true) => {
    const normalizedItemCount = Math.max(0, Math.trunc(Number(itemCount) || 0));
    const currentCount = comingFromProductForm ? await this.#resolveCurrentCartCount() : 0;
    const nextCount = comingFromProductForm ? currentCount + normalizedItemCount : normalizedItemCount;

    this.currentCartCount = nextCount;

    const isEmpty = nextCount === 0;

    this.refs.cartBubbleCount.classList.toggle('hidden', isEmpty);
    this.refs.cartBubble.classList.toggle('visually-hidden', isEmpty);
    this.classList.toggle('header-actions__cart-icon--has-cart', !isEmpty);

    try {
      sessionStorage.setItem(
        CART_COUNT_STORAGE_KEY,
        JSON.stringify({
          value: String(nextCount),
          timestamp: Date.now(),
        })
      );
    } catch (_) {
      // Storage can be blocked; rendered state remains authoritative.
    }

    if (!animate || isEmpty) return;

    await new Promise((resolve) => requestAnimationFrame(resolve));

    this.refs.cartBubble.classList.add('cart-bubble--animating');

    try {
      await onAnimationEnd(this.refs.cartBubbleText);
    } finally {
      this.refs.cartBubble.classList.remove('cart-bubble--animating');
    }
  };

  ensureCartBubbleIsCorrect = () => {
    if (!this.refs.cartBubbleCount) return;

    const storedCount = this.#readStoredCount();
    if (storedCount === null || storedCount === this.currentCartCount) return;

    void this.renderCartBubble(storedCount, false, false);
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
